import { useCallback, useEffect, useState } from "react";
import { fetchAssistAccess } from "../services/polarisService";
import {
    readCachedAssistAccess,
    writeCachedAssistAccess,
} from "./assistAccessCache";

/**
 * Nav should stay hidden until the access check finishes, unless a previous
 * successful check for this user already said Assist is allowed.
 * After a failed load, show the item (the API still blocks use).
 */
export function isAssistNavVisible(loading: boolean, available: boolean | null): boolean {
    if (available === true) {
        return true;
    }
    if (available === false) {
        return false;
    }
    return !loading;
}

/**
 * Loads whether the signed-in user may use Polaris Assist.
 */
export function useAssistAccess(): {
    available: boolean | null;
    visible: boolean;
    loading: boolean;
    error: Error | null;
} {
    const [available, setAvailable] = useState<boolean | null>(() => readCachedAssistAccess());
    const [loading, setLoading] = useState(() => readCachedAssistAccess() === null);
    const [error, setError] = useState<Error | null>(null);

    const load = useCallback(async () => {
        const cached = readCachedAssistAccess();
        if (cached === null) {
            setLoading(true);
        }
        try {
            const result = await fetchAssistAccess();
            setAvailable(result.available);
            writeCachedAssistAccess(result.available);
            setError(null);
        } catch (err) {
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    return {
        available,
        visible: isAssistNavVisible(loading, available),
        loading,
        error,
    };
}
